import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { SITE_URL } from '@/constants';
import { articleData } from '@/mocks/articles';

export default function ArtigoPage() {
  const { slug } = useParams<{ slug: string }>();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articleData.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 md:pt-32 pb-20 px-6 text-center">
          <div className="max-w-xl mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-accent-500"></i>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
              Artigo não encontrado
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              O artigo que você procura pode ter sido removido ou o link está incorreto.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Voltar ao Início
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD Structured Data */}
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        author: {
          '@type': 'Organization',
          name: 'Equipe hoobuy.beauty',
        },
        publisher: {
          '@type': 'Organization',
          name: 'hoobuy.beauty',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg',
          },
        },
        datePublished: '2026-06-25',
        dateModified: '2026-06-25',
        inLanguage: 'pt-BR',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/artigo/${article.slug}`,
        },
      }} />

      <main>
        {/* Article Header */}
        <header className="pt-24 md:pt-32 pb-10 md:pb-14 px-6 md:px-10 lg:px-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Voltar para o início</span>
            </Link>

            {/* Category tag */}
            <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
              {article.category}
            </span>

            {/* H1 */}
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Junho 2026
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                Leitura de {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                Equipe hoobuy.beauty
              </span>
            </div>
          </div>
        </header>

        {/* Article Featured Image */}
        <div className="px-6 md:px-10 lg:px-16 -mt-6 mb-10">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="px-6 md:px-10 lg:px-16 pb-14 md:pb-20">
          <div className="max-w-3xl mx-auto">
            <div className="prose-custom space-y-6">
              <div
                className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-5"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Internal links section */}
              <div className="border-t border-background-200 pt-8 mt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  Continue Lendo
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {articleData
                    .filter((a) => a.slug !== slug)
                    .slice(0, 4)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/artigo/${a.slug}`}
                        className="flex items-start gap-3 p-3 rounded-lg border border-background-200 hover:bg-background-50 transition-colors cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs text-foreground-400">{a.category}</span>
                          <p className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2 mt-0.5">
                            {a.title}
                          </p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>

              {/* Back to home CTA */}
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-6 md:p-8 text-center mt-10">
                <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-2">
                  Pronto para Explorar?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 mb-5">
                  Volte para a página inicial e descubra todas as categorias do HoobuySpreadsheet.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm md:text-base font-semibold rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-home-line"></i>
                    Voltar ao Início
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm md:text-base font-medium rounded-lg border border-foreground-200 text-foreground-700 hover:bg-background-50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Acessar HoobuySpreadsheet
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}